export type TextDiffLineKind = 'same' | 'removed' | 'added';

export interface TextDiffLine {
  kind: TextDiffLineKind;
  text: string;
}

export type FieldDiffDisplayMode = 'pending' | 'accepted' | 'rejected';

function buildPartsDiffLines(aParts: string[], bParts: string[]): TextDiffLine[] {
  const m = aParts.length;
  const n = bParts.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i][j] =
        aParts[i] === bParts[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const result: TextDiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < m || j < n) {
    if (i < m && j < n && aParts[i] === bParts[j]) {
      result.push({ kind: 'same', text: aParts[i++] });
      j++;
      // Сравнение именно в эту сторону: взять «добавлено» значит перейти в
      // dp[i][j+1], поэтому с ним и сравниваем. Обратный порядок уводил в ветку
      // с меньшим остатком LCS — после первого расхождения совпадения дальше
      // уже не находились, и хвост вываливался как «добавлено» плюс «удалено»
      // целиком, даже когда строки совпадали дословно.
    } else if (j < n && (i >= m || dp[i][j + 1] >= dp[i + 1][j])) {
      result.push({ kind: 'added', text: bParts[j++] });
    } else {
      result.push({ kind: 'removed', text: aParts[i++] });
    }
  }
  return result;
}

function splitForDiff(oldValue: string, newValue: string): { aParts: string[]; bParts: string[] } {
  const useLines = oldValue.includes('\n') || newValue.includes('\n');
  if (useLines) {
    return {
      aParts: oldValue.split('\n'),
      bParts: newValue.split('\n'),
    };
  }

  const aTrimmed = oldValue.trim();
  const bTrimmed = newValue.trim();
  if (!aTrimmed && !bTrimmed) {
    return { aParts: [], bParts: [] };
  }

  return {
    aParts: aTrimmed ? aTrimmed.split(/\s+/) : [],
    bParts: bTrimmed ? bTrimmed.split(/\s+/) : [],
  };
}

function isShortScalarField(oldValue: string, newValue: string): boolean {
  const useLines = oldValue.includes('\n') || newValue.includes('\n');
  if (useLines) return false;
  return oldValue.length <= 120 && newValue.length <= 120;
}

/** Long single-line fields are diffed by word, not by line. */
export function usesWordLevelDiff(oldValue: string, newValue: string): boolean {
  if (oldValue.includes('\n') || newValue.includes('\n')) return false;
  return !isShortScalarField(oldValue, newValue);
}

/** Full diff between two field values (line- or word-level). */
export function buildTextDiffLines(oldValue: string, newValue: string): TextDiffLine[] {
  const { aParts, bParts } = splitForDiff(oldValue, newValue);
  return buildPartsDiffLines(aParts, bParts);
}

/** Only added/removed fragments — unchanged context is omitted. */
export function getChangedTextDiffLines(oldValue: string, newValue: string): TextDiffLine[] {
  return buildTextDiffLines(oldValue, newValue).filter((line) => line.kind !== 'same');
}

/** Lines to render in NLT patch UI for a single field change. */
export function getFieldDiffDisplayLines(
  oldValue: string,
  newValue: string,
  mode: FieldDiffDisplayMode
): TextDiffLine[] {
  if (isShortScalarField(oldValue, newValue)) {
    if (mode === 'accepted') {
      return newValue !== '' ? [{ kind: 'added', text: newValue }] : [];
    }
    if (mode === 'rejected') {
      return oldValue !== '' ? [{ kind: 'removed', text: oldValue }] : [];
    }
    const lines: TextDiffLine[] = [];
    if (oldValue !== '') lines.push({ kind: 'removed', text: oldValue });
    if (newValue !== '') lines.push({ kind: 'added', text: newValue });
    return lines;
  }

  if (usesWordLevelDiff(oldValue, newValue)) {
    if (mode === 'accepted') {
      return newValue !== '' ? [{ kind: 'added', text: newValue }] : [];
    }
    if (mode === 'rejected') {
      return oldValue !== '' ? [{ kind: 'removed', text: oldValue }] : [];
    }
    const changed = getChangedTextDiffLines(oldValue, newValue);
    if (changed.length === 0) {
      const lines: TextDiffLine[] = [];
      if (oldValue !== '') lines.push({ kind: 'removed', text: oldValue });
      if (newValue !== '') lines.push({ kind: 'added', text: newValue });
      return lines;
    }
    return changed;
  }

  const changed = getChangedTextDiffLines(oldValue, newValue);
  if (changed.length === 0) {
    if (mode === 'accepted') {
      return newValue !== '' ? [{ kind: 'added', text: newValue }] : [];
    }
    if (mode === 'rejected') {
      return oldValue !== '' ? [{ kind: 'removed', text: oldValue }] : [];
    }
    const lines: TextDiffLine[] = [];
    if (oldValue !== '') lines.push({ kind: 'removed', text: oldValue });
    if (newValue !== '') lines.push({ kind: 'added', text: newValue });
    return lines;
  }

  if (mode === 'accepted') return changed.filter((line) => line.kind === 'added');
  if (mode === 'rejected') return changed.filter((line) => line.kind === 'removed');
  return changed;
}

// ─── Разметка для полноэкранного просмотра диффа (AIT-297) ───────────────────

export interface DiffStats {
  added: number;
  removed: number;
}

export function diffStats(lines: TextDiffLine[]): DiffStats {
  return lines.reduce<DiffStats>(
    (acc, line) => {
      if (line.kind === 'added') acc.added += 1;
      if (line.kind === 'removed') acc.removed += 1;
      return acc;
    },
    { added: 0, removed: 0 }
  );
}

export interface SplitDiffRow {
  /** Левая сторона — как было. `null` = строки с этой стороны нет. */
  left: string | null;
  right: string | null;
  kind: 'same' | 'changed' | 'added' | 'removed';
  /** Номера строк, каждый со своей стороны; `null` там, где строки нет. */
  leftNo: number | null;
  rightNo: number | null;
}

/**
 * Две колонки «было / стало», выровненные построчно.
 *
 * Плоская последовательность строк diff не годится для двух колонок напрямую:
 * удалённые и добавленные идут подряд, и если просто разложить их по сторонам,
 * колонки разъедутся на первом же неравном по длине куске. Поэтому идущие
 * подряд удаления и добавления собираются в пары — заменённая строка оказывается
 * напротив своей замены, а хвост более длинной стороны дополняется пустотами.
 */
export function buildSplitDiffRows(oldValue: string, newValue: string): SplitDiffRow[] {
  const lines = buildTextDiffLines(oldValue, newValue);
  const rows: SplitDiffRow[] = [];
  let leftNo = 0;
  let rightNo = 0;
  let removed: string[] = [];
  let added: string[] = [];

  const flush = () => {
    const pairs = Math.max(removed.length, added.length);
    for (let i = 0; i < pairs; i += 1) {
      const left = i < removed.length ? removed[i] : null;
      const right = i < added.length ? added[i] : null;
      rows.push({
        left,
        right,
        kind: left !== null && right !== null ? 'changed' : left !== null ? 'removed' : 'added',
        leftNo: left !== null ? (leftNo += 1) : null,
        rightNo: right !== null ? (rightNo += 1) : null,
      });
    }
    removed = [];
    added = [];
  };

  lines.forEach((line) => {
    if (line.kind === 'removed') {
      removed.push(line.text);
      return;
    }
    if (line.kind === 'added') {
      added.push(line.text);
      return;
    }
    flush();
    rows.push({
      left: line.text,
      right: line.text,
      kind: 'same',
      leftNo: (leftNo += 1),
      rightNo: (rightNo += 1),
    });
  });
  flush();
  return rows;
}

/**
 * Порядок строк в едином режиме: сначала удалённые, потом добавленные.
 *
 * Сам алгоритм внутри расхождения выдаёт добавления раньше удалений — читать
 * так непривычно: во всех diff-инструментах сперва идёт то, что было. Порядок
 * внутри блока меняется, содержимое — нет.
 */
export function toUnifiedOrder(lines: TextDiffLine[]): TextDiffLine[] {
  const out: TextDiffLine[] = [];
  let added: TextDiffLine[] = [];
  let removed: TextDiffLine[] = [];
  const flush = () => {
    out.push(...removed, ...added);
    removed = [];
    added = [];
  };
  lines.forEach((line) => {
    if (line.kind === 'added') return void added.push(line);
    if (line.kind === 'removed') return void removed.push(line);
    flush();
    out.push(line);
  });
  flush();
  return out;
}
